import React, { useMemo } from 'react';

import { useHistory } from 'react-router-dom';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';

import { useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useUserHook from 'hooks/useUserHook';

import { useUserStore } from 'store/user.store';

import { useMutationCustomerSet } from 'api/mutations/customerSet.mutation';
import { useQueryCustomerGet } from 'api/queries/customerGet.query';

import { Alert } from 'components/DesignSystem/Alert';
import { buildCustomerPayload } from 'components/Settings/Components/Opc/OpcUtils';
import { settingsConfig } from 'components/Settings/settingsConfig';
import {
    OpcCredentialsForm,
    OpcCredentialsFormValues,
} from 'components/pages/OpcCredentialsPage/components/OpcCredentials/components/OpcCredentialsForm/OpcCredentialsForm';
import { OpcHeader } from 'components/pages/OpcCredentialsPage/components/OpcCredentials/components/OpcHeader/OpcHeader';

import classes from './OpcCredentials.module.scss';

interface Props {
    isEditMode: boolean;
}

export const OpcCredentials = ({ isEditMode }: Props) => {
    const updateUserStore = useUserStore((state) => state.updateUser);
    const history = useHistory();
    const { customerId } = useUserHook();
    const { data: customerData } = useQueryCustomerGet({ customerId });
    const { mutate, isLoading } = useMutationCustomerSet();
    const { handleSnackBar } = useCustomSnackBar();

    const defaultValues = useMemo((): Partial<OpcCredentialsFormValues> | null => {
        if (!isEditMode || !customerData) {
            return null;
        }
        return {
            opcHost: customerData.opcHost ?? '',
            opcUsername: customerData.opcUsername ?? '',
        };
    }, [isEditMode, customerData]);

    const handleSubmitOpcCredentials = (formValues: OpcCredentialsFormValues): void => {
        if (!customerId || !customerData) {
            return;
        }
        const customerPayload = buildCustomerPayload({ customerId, customerData, opcValues: formValues });

        mutate(customerPayload, {
            onSuccess: () => {
                updateUserStore({ opcEnabled: true });
                handleSnackBar('Successfully updated!', 'success');
                history.push(
                    AppRoutes.buildPath(
                        AppRoutes.SETTINGS,
                        settingsConfig.integrations.link,
                        settingsConfig.opc.link,
                        settingsConfig.viewOpc.link,
                    ),
                );
            },
            onError: () => {
                handleSnackBar('Failed to update customer profile.', 'error');
            },
        });
    };

    return (
        <div className={classes.OpcCredentials}>
            <OpcHeader />
            {isEditMode && (
                <Alert severity={'warning'} sx={{ maxWidth: '835px' }}>
                    Updating credentials will replace the existing connection settings and may affect scheduled imports
                    or exports until the new connection is verified.
                </Alert>
            )}
            <OpcCredentialsForm
                isEditMode={isEditMode}
                defaultValues={defaultValues}
                onSubmit={handleSubmitOpcCredentials}
                isLoadingSubmit={isLoading}
            />
        </div>
    );
};
